import {
    ScoopSelector,
    ScoopRadioButton,
    ScoopSlider,
    ScoopSwitch,
    ScoopText,
    ScoopDivider,
    ScoopTooltip,
    ScoopColumn,
} from '../../../../../ui'
import { ArrowsSplitIcon, EyeClosedIcon, EyeIcon, FlowArrowIcon } from '@phosphor-icons/react'
import styles from './PropertiesTab.module.scss'

export const PropertiesTab = ({
    diagramSelected,
    onChangeDiagramSelected,
    diagramState,
    displayLinksAboveThreshold,
    handleFetchSankey,
    diagramSetSankeyNumberOfSteps,
    diagramSetSankeyPeriod,
    diagramSetSankeyIsStepBased,
    diagramSetAllowedStartingValues,
    generateOptionsDropdownSankeyStartingValues,
    startingValuesOptions,
    onToggleShowNodes,
    diagramSetStep,
}) => {
    return (
        <>
            <ScoopSelector
                value={diagramSelected}
                onChange={(e) => onChangeDiagramSelected(e.target.value)}
                className={styles.diagramSelector}
                options={[
                    {
                        label: 'Process Diagram',
                        value: 'process',
                        leftSection: <FlowArrowIcon size={20} weight="regular" />,
                    },
                    {
                        label: 'Sankey Chart',
                        value: 'sankey',
                        leftSection: (
                            <ArrowsSplitIcon
                                size={20}
                                weight="regular"
                                className={styles.sankeyIcon}
                            />
                        ),
                    },
                ]}
                size="sm"
            />
            {diagramSelected === 'process' ? (
                <>
                    <ScoopSlider
                        value={diagramState.weightThreshold}
                        label={'Percentage likelihood'}
                        onChange={(value) =>
                            displayLinksAboveThreshold({
                                weightThreshold: value,
                                supportThreshold: diagramState.supportThreshold,
                                linksData: diagramState.originalLinkDataArray,
                            })
                        }
                        valueLabelDisplay
                        aria-labelledby="weight-slider"
                        min={0}
                        max={100}
                        step={1}
                        marks={[{ value: 25 }, { value: 50 }, { value: 75 }]}
                    />
                    <ScoopSlider
                        label={'Number of occurrences'}
                        value={diagramState.supportThreshold}
                        onChange={(value) =>
                            displayLinksAboveThreshold({
                                supportThreshold: value,
                                weightThreshold: diagramState.weightThreshold,
                                linksData: diagramState.originalLinkDataArray,
                            })
                        }
                        min={0}
                        max={100}
                        step={1}
                        marks={[{ value: 25 }, { value: 50 }, { value: 75 }]}
                        aria-labelledby="support-slider"
                        valueLabelDisplay
                    />
                    <ScoopSwitch
                        label="To final stage"
                        name="step-switch"
                        checked={diagramState.step === 'final'}
                        onChange={(event) => {
                            diagramSetStep(event.target.checked ? 'final' : 'next')
                        }}
                        tooltip="Toggle on to see stats on each stage to 'success' "
                        direction="row"
                        className={styles.toggleRow}
                        labelClassName={styles.toggleLabel}
                    />
                    <ScoopDivider className={styles.divider} />

                    <div className={styles.nodeList}>
                        {(diagramState.originalNodeDataArray || []).map((node) => {
                            return (
                                <div
                                    key={node.node}
                                    className={styles.nodeListItem}
                                    onClick={() => onToggleShowNodes(node)}
                                >
                                    <ScoopText className={styles.nodeText}>{node.node}</ScoopText>
                                    {!node.isChecked ? (
                                        <EyeIcon size={20} />
                                    ) : (
                                        <EyeClosedIcon size={20} />
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </>
            ) : (
                <ScoopColumn className={styles.radioGroup}>
                    <ScoopRadioButton
                        label="Step based"
                        size="sm"
                        checked={diagramState.isStepBased}
                        onChange={() => {
                            diagramSetSankeyIsStepBased(true)
                            if (diagramState.diagramName) {
                                handleFetchSankey({
                                    isStepBased: true,
                                    numberOfSteps: diagramState.numberOfSteps,
                                    sankeyPeriod: diagramState.sankeyPeriod,
                                    allowedStartingValues: diagramState.allowedStartingValues,
                                })
                            }
                        }}
                        name="sankey-type"
                        value="step-based"
                    />
                    <ScoopRadioButton
                        size="sm"
                        label="Time based"
                        checked={!diagramState.isStepBased}
                        onChange={() => {
                            diagramSetSankeyIsStepBased(false)
                            if (diagramState.diagramName) {
                                handleFetchSankey({
                                    isStepBased: false,
                                    numberOfSteps: diagramState.numberOfSteps,
                                    sankeyPeriod: diagramState.sankeyPeriod,
                                    allowedStartingValues: diagramState.allowedStartingValues,
                                })
                            }
                        }}
                        name="sankey-type"
                        value="time-based"
                    />
                    {/* Selector to select number of steps Sankey */}
                    <ScoopSelector
                        label="Number of steps"
                        value={diagramState.numberOfSteps}
                        onChange={(e) => {
                            diagramSetSankeyNumberOfSteps(e.target.value)
                            if (diagramState.diagramName)
                                handleFetchSankey({
                                    isStepBased: diagramState.isStepBased,
                                    numberOfSteps: e.target.value,
                                    sankeyPeriod: diagramState.sankeyPeriod,
                                    allowedStartingValues: diagramState.allowedStartingValues,
                                })
                        }}
                        options={Array.from({ length: 15 }, (_, index) => index + 1).map(
                            (step) => ({
                                label: String(step),
                                value: step,
                            })
                        )}
                        size="sm"
                    />

                    {!diagramState.isStepBased && (
                        <ScoopSelector
                            label="Time period between steps"
                            value={diagramState.sankeyPeriod}
                            onChange={(e) => {
                                diagramSetSankeyPeriod(e.target.value)
                                if (diagramState.diagramName)
                                    handleFetchSankey({
                                        isStepBased: diagramState.isStepBased,
                                        numberOfSteps: diagramState.numberOfSteps,
                                        sankeyPeriod: e.target.value,
                                        allowedStartingValues: diagramState.allowedStartingValues,
                                    })
                            }}
                            options={[
                                { label: 'Daily', value: 'Daily' },
                                { label: 'Weekly', value: 'Weekly' },
                                { label: 'Monthly', value: 'Monthly' },
                                { label: 'Quarterly', value: 'Quarterly' },
                            ]}
                            size="sm"
                        />
                    )}

                    {/* Starting Values */}
                    <ScoopTooltip
                        title={
                            startingValuesOptions?.length === 0
                                ? 'Select a Report and an Attribute to load options'
                                : ''
                        }
                    >
                        <ScoopSelector
                            multiple
                            label="Starting values"
                            value={diagramState.allowedStartingValues ?? []}
                            disabled={startingValuesOptions?.length === 0}
                            onChange={(e) => {
                                diagramSetAllowedStartingValues(e.target.value)
                            }}
                            renderValue={(selected) => `${selected.length} Selected`}
                            options={
                                startingValuesOptions?.map((option) => ({
                                    label: option,
                                    value: option,
                                })) || []
                            }
                            size="sm"
                        />
                    </ScoopTooltip>
                </ScoopColumn>
            )}
        </>
    )
}
